/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import isEqual from 'lodash.isequal';
import merge from 'lodash.merge';
import cloneDeep from 'lodash.clonedeep';

import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import CustomerModel from 'cio/models/customer';
import { TopicWithPreference, SubscriptionPreferences } from 'cio/utils/subscription-center';
import { IdentifierKey } from 'cio/utils/identifiers';

interface ManagePreferencesModalArgs {
  customer: CustomerModel;
  topicsWithPrefs: TopicWithPreference[];
  computedCustomerPreferences: SubscriptionPreferences;
  authReadOnly: boolean;
  toggleSaveModal: () => unknown;
}

export default class ManagePreferencesModal extends Component<ManagePreferencesModalArgs> {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;

  @tracked showConfirmUnsubscribeModal = false;

  /*
    Used to track changes made to preferences for update
    This is required as to not allow assumed values when updating preferences
  */
  @tracked subscriptionPreferencesProxy: SubscriptionPreferences = {
    topics: { ...this.args.customer.subscriptionPreferences.topics },
  };

  readonly customerIdentifier = this.args.customer.email ?? this.args.customer.identifiers.id;

  get computedSubscriptionPreferencesProxy() {
    /*
      Used to determine when to enable/disable save preferences button
      It can contain computed preferences (assumed values based on topic settings)
    */
    return merge({}, this.args.computedCustomerPreferences, this.subscriptionPreferencesProxy);
  }

  get isDirty() {
    return !isEqual(this.computedSubscriptionPreferencesProxy.topics, this.args.computedCustomerPreferences.topics);
  }

  get updateDisabled() {
    return !this.isDirty || this.save.isRunning || !!this.args.authReadOnly;
  }

  @action
  updateTopicPreference(topicID: string, checked: boolean) {
    if (
      topicID in this.subscriptionPreferencesProxy.topics &&
      (this.args.customer.subscriptionPreferences.topics == null ||
        !(topicID in this.args.customer.subscriptionPreferences.topics))
    ) {
      // delete topicID if no changes needs to be made
      let newSubscriptionPreferences = cloneDeep(this.subscriptionPreferencesProxy);

      delete newSubscriptionPreferences.topics[topicID];

      this.subscriptionPreferencesProxy = newSubscriptionPreferences;
    } else {
      this.subscriptionPreferencesProxy = {
        ...this.subscriptionPreferencesProxy,
        topics: {
          ...this.subscriptionPreferencesProxy.topics,
          [topicID]: checked,
        },
      };
    }
  }

  save = dropTask(async () => {
    try {
      this.args.customer.updateSubscriptionPreferences(this.subscriptionPreferencesProxy);
      if (this.args.customer.isGloballyUnsubscribed) {
        this.args.customer.unsubscribed = false;
      }

      this.args.toggleSaveModal();
      this.alerts.success('subscription.preferences.update.success', `Subscription preferences updated`);
    } catch (err) {
      this.args.customer.rollbackAttributes();
      this.apiErrors.report(err);
    }
  });

  unsubscribe = dropTask(async () => {
    try {
      let customerIds = [this.args.customer.identifiers[IdentifierKey.CioId]];
      await this.api.post('customers/unsubscribe', {
        data: { internal_ids: customerIds },
      });

      this.showConfirmUnsubscribeModal = false;
      this.args.toggleSaveModal();
      this.args.customer.unsubscribed = true;
      this.alerts.success(
        'subscription.preferences.unsubscribe.success',
        `${this.customerIdentifier} will be unsubscribed soon`,
      );
    } catch (err) {
      this.args.customer.rollbackAttributes();
      this.apiErrors.report(err);
    }
  });

  @action
  onClose() {
    this.args.customer.rollbackAttributes();
    this.args.toggleSaveModal();
  }

  @action
  onCloseConfirmUnsubscribeModal() {
    this.showConfirmUnsubscribeModal = false;
    this.subscriptionPreferencesProxy = {
      topics: { ...this.args.customer.subscriptionPreferences.topics },
    };
  }
}
